<script lang="ts" setup>
  // External
  import { useQueryClient } from '@tanstack/vue-query';
  import type { Row } from '@tanstack/vue-table';
  import { snakeCase } from 'change-case';
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';

  // Workspace
  import type { SMSThreadSummary } from '@stuut/api-sdk/models';
  import type { SMSThreadQueryParams } from '@stuut/api/query';
  import { toast } from '@ui/components/sonner';
  import { ModelTable, TablePagination } from '@ui/components/table';

  import { usePaginatedTable } from '@/composables';
  import { sdk } from '@/sdk';

  import { columns } from './columns';

  const props = defineProps<{ useRouteQuery?: boolean }>();

  const queryParams = defineModel<SMSThreadQueryParams>({
    required: true,
  });

  const router = useRouter();
  const queryClient = useQueryClient();
  const loading = ref(false);
  const totalRecords = ref<number>();

  const paramsForKey = computed(() => {
    const raw = queryParams.value.serialize({ forApiRequest: true });
    return JSON.parse(JSON.stringify(raw));
  });

  onBeforeUnmount(() => {
    queryClient.removeQueries({
      exact: false,
      queryKey: ['sms-threads'],
    });
  });

  const queryKey = computed(() => ['sms-threads', paramsForKey.value]);

  const { table } = usePaginatedTable<SMSThreadSummary, SMSThreadQueryParams>({
    defaultSort: [{ desc: true, id: 'latestMessageAt' }],
    params: queryParams,
    queryOptions: {
      initialData: [],
      queryFn: async () => {
        loading.value = true;
        try {
          const sortBy = queryParams.value.sortBy ? snakeCase(queryParams.value.sortBy) : undefined;

          const response = await sdk.smsThreads.list({
            businessUnitIdIn: queryParams.value.business_unit_id__in,
            limit: queryParams.value.limit,
            page: queryParams.value.page,
            partnerId: queryParams.value.partner,
            search: queryParams.value.search,
            sortBy: sortBy as any,
            sortOrder: queryParams.value.sortOrder as any,
          });

          totalRecords.value = response.totalRecords ?? undefined;
          loading.value = false;
          return response.data || [];
        } catch (err) {
          loading.value = false;
          console.error('Error loading SMS threads:', err);
          toast.error('An error occurred while loading SMS threads');
          return [];
        }
      },
      queryKey,
    },
    tableOptions: {
      get columns() {
        return columns;
      },
      enableHiding: false,
      getRowId: (row) => row.id,
    },
    useRouterQuery: props.useRouteQuery,
  });

  const updateItemInCache = (item: SMSThreadSummary) => {
    queryClient.setQueriesData<SMSThreadSummary[]>({ queryKey: ['sms-threads'] }, (old) =>
      Array.isArray(old) ? old.map((i) => (i.id === item.id ? item : i)) : old,
    );
  };

  watch(paramsForKey, () => {
    queryClient.invalidateQueries({ queryKey: ['sms-threads'] });
  });

  const onRowClicked = (row: Row<SMSThreadSummary>) => {
    router.push({
      name: 'collection.sms.thread',
      params: { id: row.id },
    });
  };

  defineExpose({
    updateItemInCache,
  });
</script>

<template>
  <div
    class="grid grid-rows-[1fr_min-content] overflow-hidden h-full gap-4 relative"
    v-bind="$attrs"
  >
    <ModelTable
      :columns
      :no-search-result="!table.getRowModel().rows?.length"
      :table="table"
      :loading
      @row-click="onRowClicked"
    />

    <TablePagination :table="table" :loading no-select :total-records="totalRecords" />
  </div>
</template>

<style scoped lang="postcss"></style>
